import type { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";

import { Copy } from "@/components/icons/copy";
import { FormControl } from "@/components/shared/forms";
import type { VideoLibraryAnalyticsProps } from "@/content-hub/analytics/video-library-events";
import {
  trackLibraryVideoCopyClick,
  trackLibraryVideoDescriptionUpdate,
  trackLibraryVideoFormResetClick,
  trackLibraryVideoThumbnailChange,
  trackLibraryVideoTitleUpdate,
} from "@/content-hub/analytics/video-library-events";
import ImageUpload from "@/content-hub/editor/components/image-upload";
import type { MediaItem } from "@/content-hub/page/domain";
import { getAssetUrl } from "@/content-hub/utils";
import { copyVideoSharePageUrlAndShowToast } from "@/content-hub/utils/video-share-copied-toast";
import { useDebouncedFieldCommit } from "@/hooks/use-debounced-field-commit";

import { VideoDetailsDescriptionEditor } from "./video-details-description-editor";

const EMPTY_UPLOAD_METADATA = { id: "", path: "" } as MediaItem;

export type RecordingVideoDetailsFieldsProps = {
  displayTitle: string;
  displayDescription: string;
  displayThumbnail: string | undefined;
  /** Block `thumbnail` (e.g. oEmbed) when `displayThumbnail` is unset. */
  thumbnail?: string;
  uploadTransactionId: string;
  onTitleChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onThumbnailUpload: (imageMetadata: MediaItem) => void;
  /** Clears display overrides; recording/embed values show as placeholders and on canvas. */
  onReset: () => void;
  /** When false, Reset is hidden until a display override exists on the block. */
  showReset?: boolean;
  titlePlaceholder?: string;
  descriptionPlaceholder?: string;
  className?: string;
  /**
   * Client-computed deep link for copy in the editor only. Never read from or written
   * to Craft node props / backend — omit to hide the share row.
   * Pass `""` when the URL is not yet resolvable (pending copy).
   */
  localReadonlyShareUrl?: string;
  analytics: VideoLibraryAnalyticsProps;
};

/**
 * Reusable “Details” block: title, description, thumbnail. Parent controls values
 * (e.g. Craft `setProp`) and can mount behind LaunchDarkly / feature flags.
 */
export function RecordingVideoDetailsFields(
  props: RecordingVideoDetailsFieldsProps
) {
  const {
    displayTitle,
    displayDescription,
    displayThumbnail,
    thumbnail,
    uploadTransactionId,
    onTitleChange,
    onDescriptionChange,
    onThumbnailUpload,
    onReset,
    showReset = false,
    titlePlaceholder,
    className = "",
    localReadonlyShareUrl,
    analytics,
  } = props;
  const { t } = useTranslation();

  const titleField = useDebouncedFieldCommit(displayTitle, onTitleChange);
  const descriptionField = useDebouncedFieldCommit(
    displayDescription,
    onDescriptionChange
  );

  const shareUrlFieldId = `${uploadTransactionId}-video-share-url`;
  const customThumb = displayThumbnail?.trim();
  const blockThumb = thumbnail?.trim();
  const resolvedThumbnail = customThumb ? customThumb : blockThumb;

  return (
    <div className={`flex flex-col gap-y-3 ${className}`.trim()}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs font-medium text-neutral-b1">
          {t("content-hub.preference-panel.video-share-details")}
        </div>
        {showReset ? (
          <button
            type="button"
            className="shrink-0 border-0 bg-transparent p-0 text-xs font-medium text-blue-600 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            onClick={() => {
              trackLibraryVideoFormResetClick(analytics);
              onReset();
            }}
          >
            {t("content-hub.preference-panel.video-details-reset")}
          </button>
        ) : null}
      </div>
      <div className="flex flex-1 flex-col gap-y-3">
        {localReadonlyShareUrl !== undefined ? (
          <div className="flex flex-col gap-y-1">
            {localReadonlyShareUrl ? (
              <>
                <FormControl
                  id={shareUrlFieldId}
                  label={t("content-hub.preference-panel.video-share-url")}
                >
                  <div className="relative flex w-full items-center">
                    <input
                      id={shareUrlFieldId}
                      readOnly
                      tabIndex={-1}
                      aria-readonly="true"
                      value={localReadonlyShareUrl}
                      className="w-full cursor-default truncate rounded-lg border border-solid border-neutral-b4 bg-white py-2 pl-2 pr-9 text-xs text-neutral-b1 outline-none"
                    />
                    <button
                      type="button"
                      className="absolute right-1 flex rounded p-1 text-neutral-b2 hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                      aria-label={t("preference-panel.copy-video-share-link")}
                      onClick={() => {
                        trackLibraryVideoCopyClick(analytics);
                        void copyVideoSharePageUrlAndShowToast(
                          localReadonlyShareUrl
                        );
                      }}
                    >
                      <Copy className="size-4" aria-hidden />
                    </button>
                  </div>
                </FormControl>
                <p className="text-xs text-neutral-b2">
                  {t("content-hub.preference-panel.video-share-url-helper")}
                </p>
              </>
            ) : (
              <p className="text-xs text-neutral-b2">
                {t("content-hub.preference-panel.video-share-url-pending")}
              </p>
            )}
          </div>
        ) : null}
        <div className="flex flex-1 flex-col gap-y-2">
          <div className="text-xs font-medium text-neutral-b1">
            {t("content-hub.preference-panel.video-title-field")}
          </div>
          <input
            type="text"
            value={titleField.localValue}
            className="flex h-9 items-center rounded-lg border border-solid border-black/20 p-3 text-sm focus:border-primary focus:outline-none"
            placeholder={titlePlaceholder}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              titleField.onChange(e.target.value);
              trackLibraryVideoTitleUpdate(analytics);
            }}
            onBlur={() => {
              titleField.onBlur();
              trackLibraryVideoTitleUpdate.flush();
            }}
          />
        </div>
        <div className="flex flex-1 flex-col gap-y-2">
          <div className="text-xs font-medium text-neutral-b1">
            {t("content-hub.preference-panel.video-description-field")}
          </div>
          <VideoDetailsDescriptionEditor
            editorKey={`${uploadTransactionId}-description`}
            value={descriptionField.localValue}
            onChange={(value) => {
              descriptionField.onChange(value);
              trackLibraryVideoDescriptionUpdate(analytics);
            }}
            onBlur={() => {
              descriptionField.onBlur();
              trackLibraryVideoDescriptionUpdate.flush();
            }}
          />
        </div>
        <div className="flex flex-1 flex-col gap-y-2">
          <div className="text-xs font-medium text-neutral-b1">
            {t("content-hub.preference-panel.video-thumbnail-field")}
          </div>
          <ImageUpload
            uploadTransactionId={uploadTransactionId}
            imageMetadata={EMPTY_UPLOAD_METADATA}
            onUpload={(imageMetadata) => {
              trackLibraryVideoThumbnailChange(analytics);
              onThumbnailUpload(imageMetadata);
            }}
            defaultImage={resolvedThumbnail}
          />
        </div>
      </div>
    </div>
  );
}

export function displayThumbnailUrlFromUpload(
  imageMetadata: MediaItem
): string {
  return getAssetUrl(imageMetadata.path);
}
